<template>
  <div class="task-list-container">
    <!-- 移动端任务列表 -->
    <div class="mobile-task-list">
      <div class="navbar">
        <h1 class="navbar-title">TaskFlow</h1>
        <a-icon type="user" class="navbar-avatar" />
      </div>

      <div class="task-stats">
        <div class="stat-item">
          <span class="stat-number">{{ taskStats.total }}</span>
          <span class="stat-label">总任务</span>
        </div>
        <div class="stat-item">
          <span class="stat-number">{{ taskStats.pending }}</span>
          <span class="stat-label">待处理</span>
        </div>
        <div class="stat-item">
          <span class="stat-number">{{ taskStats.completed }}</span>
          <span class="stat-label">已完成</span>
        </div>
      </div>

      <div class="filter-tags">
        <a-tag v-for="tag in filterTags" :key="tag.value" :color="tag.color" :class="{ active: activeFilter === tag.value }" @click="activeFilter = tag.value">
          {{ tag.label }}
        </a-tag>
      </div>

      <div class="task-list">
        <div v-for="task in filteredTasks" :key="task.id" class="task-item" @click="handleTaskClick(task)">
          <div class="task-checkbox">
            <a-checkbox v-model="task.completed" @change="handleTaskStatusChange(task)" />
          </div>
          <div class="task-content">
            <h3 class="task-title" :class="{ completed: task.completed }">{{ task.title }}</h3>
            <p class="task-description">{{ task.description }}</p>
            <div class="task-meta">
              <span class="task-priority" :class="task.priority">{{ task.priority }}</span>
              <span class="task-date">{{ task.date }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="floating-button" @click="handleAddTask">
        <a-icon type="plus" />
      </div>
    </div>

    <!-- PC端任务列表 -->
    <div class="pc-task-list">
      <div class="sidebar">
        <div class="sidebar-header">
          <div class="logo-icon">✓</div>
          <h1 class="logo-text">TaskFlow</h1>
        </div>
        <div class="sidebar-menu">
          <a-menu :selected-keys="['task-list']" @click="handleMenuClick">
            <a-menu-item key="task-list"> <a-icon type="appstore" /> 任务管理 </a-menu-item>
            <a-menu-item key="statistics"> <a-icon type="bar-chart" /> 数据统计 </a-menu-item>
            <a-menu-item key="user-management"> <a-icon type="team" /> 用户管理 </a-menu-item>
            <a-menu-item key="settings"> <a-icon type="setting" /> 设置 </a-menu-item>
          </a-menu>
        </div>
        <div class="sidebar-footer">
          <div class="user-info">
            <a-avatar icon="user" />
            <span class="user-name">{{ user.email }}</span>
          </div>
          <a-button type="text" @click="handleLogout">退出登录</a-button>
        </div>
      </div>

      <div class="main-content">
        <div class="main-header">
          <h2 class="page-title">任务列表</h2>
          <a-button type="primary" @click="handleAddTask"> <a-icon type="plus" /> 新建任务 </a-button>
        </div>

        <div class="task-stats">
          <div class="stat-item">
            <span class="stat-number">{{ taskStats.total }}</span>
            <span class="stat-label">总任务</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ taskStats.pending }}</span>
            <span class="stat-label">待处理</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ taskStats.completed }}</span>
            <span class="stat-label">已完成</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ taskStats.overdue }}</span>
            <span class="stat-label">逾期</span>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-tags">
            <a-tag v-for="tag in filterTags" :key="tag.value" :color="tag.color" :class="{ active: activeFilter === tag.value }" @click="activeFilter = tag.value">
              {{ tag.label }}
            </a-tag>
          </div>
          <div class="search-box">
            <a-input v-model="searchKeyword" placeholder="搜索任务..." prefix="search" />
          </div>
        </div>

        <div class="task-list">
          <a-table :data-source="filteredTasks" :columns="tableColumns" row-key="id">
            <template slot="title" slot-scope="text, record">
              <div>
                <div :class="{ completed: record.completed }">{{ text }}</div>
                <div class="task-description">{{ record.description }}</div>
              </div>
            </template>
            <template slot="priority" slot-scope="text">
              <a-tag :color="text === 'high' ? 'red' : text === 'medium' ? 'orange' : 'green'">
                {{ text === 'high' ? '高' : text === 'medium' ? '中' : '低' }}
              </a-tag>
            </template>
            <template slot="status" slot-scope="text">
              <a-tag :color="text ? 'green' : 'blue'">
                {{ text ? '已完成' : '待处理' }}
              </a-tag>
            </template>
            <template slot="actions" slot-scope="record">
              <a-button size="small" @click="handleTaskClick(record)">编辑</a-button>
              <a-button size="small" type="danger" @click="handleDeleteTask(record.id)">删除</a-button>
            </template>
          </a-table>
        </div>
      </div>
    </div>

    <!-- 任务详情/编辑弹窗 -->
    <a-modal v-model="taskModalVisible" :title="isEditing ? '编辑任务' : '新建任务'" @ok="handleSaveTask" @cancel="taskModalVisible = false">
      <a-form :form="taskForm">
        <a-form-item label="任务标题">
          <a-input v-decorator="['title', { rules: [{ required: true, message: '请输入任务标题' }] }]" />
        </a-form-item>
        <a-form-item label="任务描述">
          <a-textarea v-decorator="['description']" rows="4" />
        </a-form-item>
        <a-form-item label="优先级">
          <a-select v-decorator="['priority']">
            <a-select-option value="high">高</a-select-option>
            <a-select-option value="medium">中</a-select-option>
            <a-select-option value="low">低</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="截止日期">
          <a-date-picker v-decorator="['date']" style="width: 100%" />
        </a-form-item>
        <a-form-item label="状态">
          <a-checkbox v-decorator="['completed']">已完成</a-checkbox>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // 用户信息
      user: JSON.parse(localStorage.getItem('user') || '{}'),

      // 任务数据
      tasks: [],

      // 任务统计
      taskStats: {
        total: 0,
        pending: 0,
        completed: 0,
        overdue: 0
      },

      // 筛选标签
      filterTags: [
        { value: 'all', label: '全部', color: '' },
        { value: 'pending', label: '待处理', color: 'blue' },
        { value: 'completed', label: '已完成', color: 'green' },
        { value: 'high', label: '高优先级', color: 'red' }
      ],

      // 当前激活的筛选
      activeFilter: 'all',
      searchKeyword: '',

      // 任务弹窗
      taskModalVisible: false,
      isEditing: false,
      currentTask: null,
      taskForm: this.$form.createForm(this)
    };
  },
  computed: {
    filteredTasks() {
      let result = [...this.tasks];

      // 筛选
      if (this.activeFilter !== 'all') {
        if (this.activeFilter === 'pending') {
          result = result.filter(task => !task.completed);
        } else if (this.activeFilter === 'completed') {
          result = result.filter(task => task.completed);
        } else {
          result = result.filter(task => task.priority === this.activeFilter);
        }
      }

      // 搜索
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase();
        result = result.filter(task => task.title.toLowerCase().includes(keyword) || task.description.toLowerCase().includes(keyword));
      }

      return result;
    },

    tableColumns() {
      return [
        {
          title: '任务标题',
          dataIndex: 'title',
          key: 'title',
          scopedSlots: {
            customRender: 'title'
          }
        },
        {
          title: '优先级',
          dataIndex: 'priority',
          key: 'priority',
          scopedSlots: {
            customRender: 'priority'
          }
        },
        {
          title: '截止日期',
          dataIndex: 'date',
          key: 'date'
        },
        {
          title: '状态',
          dataIndex: 'completed',
          key: 'completed',
          scopedSlots: {
            customRender: 'status'
          }
        },
        {
          title: '操作',
          key: 'actions',
          scopedSlots: {
            customRender: 'actions'
          }
        }
      ];
    }
  },
  mounted() {
    // 加载任务数据
    this.loadTasks();
  },
  methods: {
    // 加载任务数据
    loadTasks() {
      this.$http
        .get('/api/tasks')
        .then(response => {
          if (response.success) {
            this.tasks = response.data.map(task => ({
              ...task,
              completed: task.status === 'completed',
              date: task.dueDate
            }));
            this.updateTaskStats();
          }
        })
        .catch(error => {
          console.error('加载任务失败:', error);
          this.$message.error('加载任务失败，请稍后重试');
        });
    },
    // 处理任务点击
    handleTaskClick(task) {
      this.isEditing = true;
      this.currentTask = task;
      this.taskForm.setFieldsValue({
        title: task.title,
        description: task.description,
        priority: task.priority,
        date: task.date,
        completed: task.completed
      });
      this.taskModalVisible = true;
    },

    // 处理添加任务
    handleAddTask() {
      this.isEditing = false;
      this.currentTask = null;
      this.taskForm.resetFields();
      this.taskForm.setFieldsValue({
        priority: 'medium',
        completed: false
      });
      this.taskModalVisible = true;
    },

    // 处理保存任务
    handleSaveTask() {
      this.taskForm.validate((errors, values) => {
        if (!errors) {
          const taskData = {
            title: values.title,
            description: values.description,
            priority: values.priority,
            status: values.completed ? 'completed' : 'pending',
            dueDate: values.date
          };

          if (this.isEditing) {
            // 更新任务
            this.$http
              .put(`/api/tasks/${this.currentTask.id}`, taskData)
              .then(response => {
                if (response.success) {
                  this.loadTasks();
                  this.taskModalVisible = false;
                  this.$message.success('任务更新成功');
                } else {
                  this.$message.error('任务更新失败');
                }
              })
              .catch(error => {
                console.error('更新任务失败:', error);
                this.$message.error('更新任务失败，请稍后重试');
              });
          } else {
            // 新增任务
            this.$http
              .post('/api/tasks', taskData)
              .then(response => {
                if (response.success) {
                  this.loadTasks();
                  this.taskModalVisible = false;
                  this.$message.success('任务创建成功');
                } else {
                  this.$message.error('任务创建失败');
                }
              })
              .catch(error => {
                console.error('创建任务失败:', error);
                this.$message.error('创建任务失败，请稍后重试');
              });
          }
        }
      });
    },

    // 处理任务状态变更
    handleTaskStatusChange(task) {
      this.updateTaskStats();
    },

    // 处理删除任务
    handleDeleteTask(taskId) {
      this.$confirm({
        title: '确认删除',
        content: '确定要删除这个任务吗？',
        onOk: () => {
          this.$http
            .delete(`/api/tasks/${taskId}`)
            .then(response => {
              if (response.success) {
                this.loadTasks();
                this.$message.success('任务删除成功');
              } else {
                this.$message.error('任务删除失败');
              }
            })
            .catch(error => {
              console.error('删除任务失败:', error);
              this.$message.error('删除任务失败，请稍后重试');
            });
        }
      });
    },

    // 更新任务统计
    updateTaskStats() {
      this.taskStats = {
        total: this.tasks.length,
        pending: this.tasks.filter(task => !task.completed).length,
        completed: this.tasks.filter(task => task.completed).length,
        overdue: this.tasks.filter(task => !task.completed && new Date(task.date) < new Date()).length
      };
    },

    // 处理菜单点击
    handleMenuClick({ key }) {
      if (key === 'statistics') {
        this.$router.push('/taskflow/statistics');
      } else if (key === 'user-management') {
        this.$router.push('/taskflow/user-management');
      }
    },

    // 处理退出登录
    handleLogout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/taskflow/login');
    }
  }
};
</script>

<style lang="less">
.task-list-container {
  min-height: 100vh;
  background-color: #f5f5f5;

  .mobile-task-list {
    display: none;
    max-width: 375px;
    width: 100%;
    min-height: 100vh;
    background-color: #f5f5f5;
    margin: 0 auto;

    .navbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: #fff;
      height: 60px;
      padding: 0 20px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      .navbar-title {
        font-size: 18px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .navbar-avatar {
        font-size: 24px;
        color: #4caf50;
      }
    }

    .task-stats {
      display: flex;
      background-color: #fff;
      margin: 16px;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      .stat-item {
        flex: 1;
        text-align: center;

        .stat-number {
          display: block;
          font-size: 20px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 12px;
          color: #666666;
        }
      }
    }

    .filter-tags {
      display: flex;
      gap: 8px;
      padding: 0 20px 16px;
      overflow-x: auto;

      .ant-tag {
        white-space: nowrap;

        &.active {
          background-color: #4caf50;
          color: #fff;
        }
      }
    }

    .task-list {
      flex: 1;
      padding: 0 20px 80px;

      .task-item {
        display: flex;
        background-color: #fff;
        margin-bottom: 12px;
        padding: 16px;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        cursor: pointer;

        .task-checkbox {
          margin-right: 12px;
        }

        .task-content {
          flex: 1;

          .task-title {
            font-size: 14px;
            font-weight: 500;
            color: #1a1a1a;
            margin-bottom: 8px;

            &.completed {
              text-decoration: line-through;
              color: #999999;
            }
          }

          .task-description {
            font-size: 12px;
            color: #666666;
            margin-bottom: 8px;
            line-height: 1.4;
          }

          .task-meta {
            display: flex;
            align-items: center;
            gap: 8px;

            .task-priority {
              font-size: 10px;
              padding: 2px 8px;
              border-radius: 10px;

              &.high {
                background-color: #ffebee;
                color: #f44336;
              }

              &.medium {
                background-color: #fff3e0;
                color: #ff9800;
              }

              &.low {
                background-color: #e8f5e9;
                color: #4caf50;
              }
            }

            .task-date {
              font-size: 10px;
              color: #999999;
            }
          }
        }
      }
    }

    .floating-button {
      position: fixed;
      bottom: 24px;
      right: 24px;
      width: 56px;
      height: 56px;
      background-color: #4caf50;
      border-radius: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
      cursor: pointer;

      .anticon {
        color: #fff;
        font-size: 24px;
      }
    }
  }

  .pc-task-list {
    display: flex;
    min-height: 100vh;

    .sidebar {
      width: 240px;
      background-color: #fff;
      border-right: 1px solid #e5e5e5;
      display: flex;
      flex-direction: column;

      .sidebar-header {
        padding: 24px;
        border-bottom: 1px solid #e5e5e5;

        .logo-icon {
          font-size: 32px;
          font-weight: 700;
          color: #4caf50;
          margin-bottom: 8px;
        }

        .logo-text {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
        }
      }

      .sidebar-menu {
        flex: 1;
        padding: 16px 0;

        .ant-menu {
          border-right: none;
        }
      }

      .sidebar-footer {
        padding: 24px;
        border-top: 1px solid #e5e5e5;

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;

          .user-name {
            font-size: 14px;
            color: #1a1a1a;
          }
        }
      }
    }

    .main-content {
      flex: 1;
      padding: 32px;

      .main-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 24px;

        .page-title {
          font-size: 24px;
          font-weight: 600;
          color: #1a1a1a;
        }
      }

      .task-stats {
        display: flex;
        gap: 24px;
        margin-bottom: 24px;

        .stat-item {
          flex: 1;
          background-color: #fff;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          text-align: center;

          .stat-number {
            display: block;
            font-size: 24px;
            font-weight: 600;
            color: #1a1a1a;
            margin-bottom: 8px;
          }

          .stat-label {
            font-size: 14px;
            color: #666666;
          }
        }
      }

      .filter-section {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 24px;

        .filter-tags {
          display: flex;
          gap: 8px;

          .ant-tag {
            &.active {
              background-color: #4caf50;
              color: #fff;
            }
          }
        }

        .search-box {
          width: 300px;
        }
      }

      .task-list {
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        .task-description {
          font-size: 12px;
          color: #666666;
          margin-top: 4px;
        }

        .completed {
          text-decoration: line-through;
          color: #999999;
        }
      }
    }
  }

  // 响应式设计
  @media (max-width: 768px) {
    .mobile-task-list {
      display: block;
    }

    .pc-task-list {
      display: none;
    }
  }
}
</style>
